import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Alert, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useNFe, useTransmitirNFe } from '../../../../hooks/useNFe';

interface BotaoTransmitirNFeProps {
  emissaoId: string;
}

const BotaoTransmitirNFe: React.FC<BotaoTransmitirNFeProps> = ({ emissaoId }) => {
  const { data: nfe } = useNFe(emissaoId);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const mutation = useTransmitirNFe(emissaoId);
  const [show, setShow] = useState(false);
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  useEffect(() => {
    if (show) setApiErrors([]);
  }, [show]);

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!nfe) return errors;

    if (!nfe.destinatario) {
      errors.push('Destinatário não informado');
    } else {
      if (!nfe.destinatario.nomeRazaoSocial) errors.push('Nome/Razão Social do destinatário é obrigatório');
      if (!nfe.destinatario.cnpj && !nfe.destinatario.cpf) errors.push('CNPJ ou CPF do destinatário é obrigatório');
      if (!nfe.destinatario.logradouro) errors.push('Endereço do destinatário é obrigatório');
      if (!nfe.destinatario.municipio) errors.push('Município do destinatário é obrigatório');
      if (!nfe.destinatario.uf) errors.push('UF do destinatário é obrigatória');
      if (!nfe.destinatario.cep) errors.push('CEP do destinatário é obrigatório');
    }

    if (!nfe.itens || nfe.itens.length === 0) {
      errors.push('Nenhum item adicionado à NFe');
    } else {
      nfe.itens.forEach((item, index) => {
        if (!item.nomeProduto) errors.push(`Item ${index + 1}: Nome do produto é obrigatório`);
        if (!item.ncm) errors.push(`Item ${index + 1}: NCM é obrigatório`);
        if (!item.cfop) errors.push(`Item ${index + 1}: CFOP é obrigatório`);
        if (!item.quantidadeComercial || item.quantidadeComercial <= 0) errors.push(`Item ${index + 1}: Quantidade deve ser maior que zero`);
        if (item.valorUnitario === undefined || item.valorUnitario < 0) errors.push(`Item ${index + 1}: Valor unitário é obrigatório`);
      });
    }

    return errors;
  }, [nfe]);

  const isValid = validationErrors.length === 0;

  const handleTransmitir = async () => {
    setApiErrors([]);
    try {
      const data = await mutation.mutateAsync(undefined);
      showToast(`NF-e autorizada! Protocolo: ${data.protocolo}`, 'success');
      setShow(false);
      navigate('/operacional/nfe');
    } catch (error: any) {
      const message: string = error?.response?.data?.message ?? error?.message ?? 'Erro ao transmitir NF-e';
      const lines = message.split('\n').map((l: string) => l.trim()).filter(Boolean);
      setApiErrors(lines);
    }
  };

  const handleClose = () => {
    if (!mutation.isPending) setShow(false);
  };

  const formatarMoeda = (valor?: number) => {
    if (valor === undefined || valor === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setShow(true)}>
        <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
        Transmitir
      </Button>

      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton className="border-bottom border-opacity-25">
          <Modal.Title className="text-body-emphasis">Transmitir NF-e ao SEFAZ</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {apiErrors.length > 0 ? (
            <>
              <div className="text-center mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-danger" />
              </div>
              <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
                <div className="text-danger-emphasis">
                  <strong>Erro ao transmitir NF-e.</strong> Corrija os problemas abaixo e tente novamente.
                </div>
              </Alert>
              <div className="mb-0">
                <h6 className="text-body-emphasis mb-2">Erros de validação:</h6>
                <ListGroup variant="flush">
                  {apiErrors.map((error, index) => (
                    <ListGroup.Item
                      key={index}
                      className="border border-danger border-opacity-25 bg-danger-subtle px-3 py-2 mb-2"
                    >
                      <div className="text-danger-emphasis small">• {error}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            </>
          ) : isValid ? (
            <>
              <div className="text-center mb-3">
                <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success" />
              </div>
              <Alert variant="success" className="border border-success border-opacity-25 bg-success-subtle">
                <div className="text-success-emphasis">
                  <strong>Tudo pronto!</strong> A NF-e está válida e será enviada ao SEFAZ.
                </div>
              </Alert>
              <div className="mb-3">
                <h6 className="text-body-emphasis mb-2">Resumo da NF-e #{nfe?.numero}</h6>
                <ListGroup variant="flush">
                  <ListGroup.Item className="d-flex justify-content-between px-0">
                    <span className="text-body-secondary">Destinatário:</span>
                    <span className="text-body-emphasis">{nfe?.destinatario?.nomeRazaoSocial}</span>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between px-0">
                    <span className="text-body-secondary">Quantidade de Itens:</span>
                    <span className="text-body-emphasis">{nfe?.itens?.length || 0}</span>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between px-0">
                    <span className="text-body-secondary">Valor Total:</span>
                    <span className="text-primary-emphasis fw-semibold">{formatarMoeda(nfe?.totais?.valorNota)}</span>
                  </ListGroup.Item>
                </ListGroup>
              </div>
              <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-0">
                <div className="text-warning-emphasis small">
                  Após a transmissão, a NF-e não poderá mais ser editada. Verifique se todas as informações estão corretas.
                </div>
              </Alert>
            </>
          ) : (
            <>
              <div className="text-center mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning" />
              </div>
              <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle">
                <div className="text-warning-emphasis">
                  <strong>Atenção!</strong> A NF-e possui pendências que devem ser corrigidas antes de transmitir.
                </div>
              </Alert>
              <div className="mb-0">
                <h6 className="text-body-emphasis mb-2">Erros encontrados:</h6>
                <ListGroup variant="flush">
                  {validationErrors.map((error, index) => (
                    <ListGroup.Item
                      key={index}
                      className="border border-danger border-opacity-25 bg-danger-subtle px-3 py-2 mb-2"
                    >
                      <div className="text-danger-emphasis small">• {error}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top border-opacity-25">
          <Button variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
            {isValid && apiErrors.length === 0 ? 'Cancelar' : 'Voltar'}
          </Button>
          {isValid && apiErrors.length === 0 && (
            <Button
              variant="success"
              onClick={handleTransmitir}
              loading={mutation.isPending}
              loadingPosition="start"
            >
              {mutation.isPending ? 'Transmitindo...' : 'Transmitir'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BotaoTransmitirNFe;
